import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Beaker, Leaf, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { References } from "@/components/References";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import magSleepHero from "@/assets/blog/mag-sleep-hero.png.asset.json";
import magSleepMechanism from "@/assets/blog/mag-sleep-mechanism.png.asset.json";
import magTiming from "@/assets/blog/mag-timing.png.asset.json";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";

export const Route = createFileRoute("/blog/magnesium-benefits")({
  component: MagnesiumBenefitsArticle,
  head: () => buildBlogHead({
    slug: "magnesium-benefits",
    title: "Magnesium Benefits: Forms, Foods & Dosage Guide | TruWe",
    description:
      "Magnesium runs 300+ enzyme reactions. Compare magnesium forms, food sources and the right daily dose for sleep, cramps and recovery.",
    ogTitle: "Magnesium Benefits: Forms, Foods & Dosage Guide",
    ogDescription:
      "Magnesium runs 300+ enzyme reactions. Compare magnesium forms, food sources and the right daily dose for sleep, cramps and recovery.",
    headline: "Magnesium Benefits: Forms, Foods & Dosage Guide",
    breadcrumbName: "Magnesium Benefits",
    datePublished: "2026-06-11",
    dateModified: "2026-07-25",
    faqs: [
      {
        q: "What is the best form of magnesium?",
        a: "Magnesium bisglycinate is best tolerated for sleep and recovery. Aquamin® (marine multi-mineral), chloride and aspartate are absorbed efficiently and support muscle and energy. Oxide is the cheapest but the least bioavailable.",
      },
      {
        q: "How much magnesium per day?",
        a: "The RDA is 310-420 mg/day for adults. Most Indian diets fall 100-200 mg short. Supplementing 200-400 mg of elemental magnesium covers the typical gap.",
      },
      {
        q: "Which foods are highest in magnesium?",
        a: "Pumpkin seeds, almonds, spinach, cashews, peanuts, black beans, edamame, dark chocolate (70%+), avocado and bananas.",
      },
    ],
  }),
});

function MagnesiumBenefitsArticle() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Minerals
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            Magnesium Benefits: Forms, Foods & Dosage Guide
          </h1>
          <BlogByline date="2026-06-11" readingTime="10 min read" />
          <div className="mt-4"><ReadWithLlm title="Magnesium Benefits" url="https://truwe.in/blog/magnesium-benefits" kind="article" /></div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Magnesium runs more than 300 enzyme reactions, from making energy (ATP) to relaxing
            muscles, calming the nervous system and locking in deep sleep. Yet roughly half of
            Indian adults consume less than the recommended daily intake. Here's what the form,
            food sources and dose actually look like in 2026.
          </p>
          <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={magSleepHero.url}
              alt="Best magnesium supplement for sleep, a complete guide to better rest naturally"
              width={1920}
              height={1080}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            What magnesium does in the body
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Energy production.</strong> Every molecule of ATP, the cell's energy
              currency, must be bound to a magnesium ion to be biologically active.
            </li>
            <li>
              <strong>Muscle relaxation.</strong> Magnesium competes with calcium at the muscle
              fibre; low magnesium = cramps, twitches and tight shoulders after workouts.
            </li>
            <li>
              <strong>Sleep & nervous system.</strong> It regulates GABA, the calming
              neurotransmitter, and quiets the HPA stress axis at night.
            </li>
            <li>
              <strong>Blood sugar & insulin sensitivity.</strong> Magnesium is a required cofactor
              for the insulin receptor's tyrosine kinase activity.
            </li>
            <li>
              <strong>Bone & cardiovascular health.</strong> 60% of body magnesium sits in bone;
              it also helps maintain normal blood pressure and heart rhythm.
            </li>
          </ul>
          <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={magSleepMechanism.url}
              alt="How magnesium improves sleep quality, enhances GABA binding, supports melatonin and regulates the HPA stress axis"
              width={1920}
              height={1080}
              loading="eager"
              className="h-auto w-full"
            />
            <figcaption className="border-t border-border bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
              Magnesium ions calm neural excitation at GABA-A receptors and support melatonin rhythm.
            </figcaption>
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Foods high in magnesium
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            Aim to combine 2-3 of these every day. The numbers below are elemental magnesium per
            100 g (USDA FoodData Central, 2024):
          </p>
          <div className="mt-5 overflow-hidden rounded-2xl border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Food</th>
                  <th className="px-4 py-3">Magnesium (per 100 g)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {[
                  ["Pumpkin seeds (roasted)", "550 mg"],
                  ["Chia seeds", "335 mg"],
                  ["Almonds", "270 mg"],
                  ["Cashews", "260 mg"],
                  ["Dark chocolate (70-85%)", "228 mg"],
                  ["Peanuts", "168 mg"],
                  ["Black beans (cooked)", "70 mg"],
                  ["Spinach (cooked)", "87 mg"],
                  ["Edamame", "64 mg"],
                  ["Avocado", "29 mg"],
                  ["Banana", "27 mg"],
                ].map(([food, mg]) => (
                  <tr key={food} className="bg-background">
                    <td className="px-4 py-3 font-medium text-brand-deep">{food}</td>
                    <td className="px-4 py-3 text-foreground/80">{mg}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Even a "good" Indian thali dal, sabzi, two rotis, salad delivers ~180-220 mg.
            The RDA is 400 mg for men and 310 mg for women. The gap is real.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The best magnesium supplement: form matters more than dose
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            "500 mg of magnesium" on a label can mean anything from cheap oxide (4% absorbed) to
            chelated bisglycinate (80%+ absorbed). Here's how the common forms stack up.
          </p>
          <div className="mt-5 space-y-4">
            {[
              {
                name: "Magnesium Bisglycinate",
                use: "Sleep, anxiety, PMS, gentle on the gut",
                note: "Chelated to two glycine molecules, glycine itself is calming, so it doubles down on sleep quality.",
              },
              {
                name: "Aquamin® (marine multi-mineral)",
                use: "Bone, joint, alkalising mineral matrix",
                note: "Sourced from red algae (Lithothamnion). Delivers magnesium alongside 72 trace minerals in a honeycomb structure.",
              },
              {
                name: "Magnesium Chloride",
                use: "Rapid absorption, muscle recovery",
                note: "One of the most bioavailable inorganic forms. Useful for athletes and post-workout cramps.",
              },
              {
                name: "Magnesium Aspartate",
                use: "Energy, fatigue, exercise performance",
                note: "Aspartic acid shuttles magnesium into mitochondria, pairs naturally with the energy pathway.",
              },
              {
                name: "Magnesium Gluconate",
                use: "Daily maintenance, gentle on digestion",
                note: "Highest oral bioavailability among the simple salts in head-to-head studies.",
              },
              {
                name: "Magnesium Oxide / Sulfate",
                use: "Avoid for daily use",
                note: "4-10% absorption. Mostly acts as a laxative. Cheap fillers in many drugstore brands.",
              },
            ].map((row) => (
              <div
                key={row.name}
                className="rounded-2xl border border-border bg-card p-5 shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-lg font-semibold text-brand-deep">{row.name}</h3>
                </div>
                <p className="mt-1 text-sm font-medium text-brand">{row.use}</p>
                <p className="mt-2 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            How much magnesium per day?
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            ICMR and the US NIH converge on ~310 mg/day for women and ~400 mg/day for men. If you
            train hard, sweat in tropical heat, drink coffee or alcohol, or take a PPI for acidity
, you're losing more and your needs go up. A practical supplement window is{" "}
            <strong>200-400 mg of elemental magnesium per day</strong>, split across the day if
            possible, with the larger dose 60-90 minutes before bed.
          </p>
          <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={magTiming.url}
              alt="Optimal timing and considerations for magnesium supplementation, take 30-60 minutes before bed with water, avoid calcium-rich foods"
              width={1920}
              height={1080}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14 rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              <Sparkles className="h-4 w-4" /> The TruWe formulation
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              Mag Absorb Pro, five forms, one stack
            </h2>
            <p className="mt-3 text-base text-foreground/85">
              Most brands ship a single cheap salt. Mag Absorb Pro combines{" "}
              <strong>Aquamin®, magnesium chloride, magnesium aspartate and magnesium gluconate</strong>{" "}
              so every transport pathway is covered, bone matrix, mitochondrial energy, muscle
              relaxation and daily maintenance, at research-backed doses.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                { icon: Leaf, label: "Plant-derived Aquamin®" },
                { icon: ShieldCheck, label: "Third-party tested" },
                { icon: Beaker, label: "USFDA / GMP / FSSAI facility" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 rounded-xl bg-background/70 px-3 py-3 text-sm text-foreground/85 ring-1 ring-border"
                >
                  <Icon className="h-4 w-4 text-brand" />
                  {label}
                </div>
              ))}
            </div>
            <Link
              to="/products/$handle"
              params={{ handle: "mag-absorb-pro" }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-luxury transition hover:bg-brand-deep"
            >
              Explore Mag Absorb Pro <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            FAQs
          </h2>
          <div className="mt-4 space-y-5 text-base text-foreground/85">
            <div>
              <h3 className="font-semibold text-brand-deep">Can I just eat more spinach?</h3>
              <p className="mt-1">
                You can close part of the gap, but oxalates in spinach and phytates in whole grains
                block 30-50% of the magnesium. Pumpkin seeds, cashews and dark chocolate are more
                bioavailable food sources.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-brand-deep">When should I take magnesium?</h3>
              <p className="mt-1">
                For sleep, 60-90 minutes before bed with a small fat (a few nuts works). For
                muscle recovery, within 30 minutes of training.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-brand-deep">Is magnesium safe daily?</h3>
              <p className="mt-1">
                Yes, up to the tolerable upper intake of 350 mg/day from supplements (food sources
                aren't capped). Going higher with oxide/sulfate can cause loose stools.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="magnesium" />
          <ClusterLinks cluster="magnesium" />
          <AuthorityFlow cluster="magnesium" />
          <References slug="magnesium-benefits" />
          <RelatedReading articles={relatedArticles("magnesium-benefits")} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            This article is for general education only and is not a substitute for medical advice.
            Consult a qualified clinician before starting any supplement, especially if you take
            medication or have a kidney condition.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
